import React, { Dispatch, FC, SetStateAction, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { ChevronDown, Edit2 } from 'lucide-react';

import { IClient, IConsultation } from '../../../store/types';
import { declOfNum } from '../../../utils/decOfNum';
import { AllSessions } from '../all-sessions';
import { SessionDetail, buildSessionDetailSession } from '../session-detail';
import { PrepHighlights } from '../prep-highlights';
import { ClientProfile } from '../ClientProfile';
import { TreatmentPlan } from '../treatment-plan';
import { Progress } from '../progress';
import { Supervision } from '../supervision';
import { EmptyState, TabList, PaginationControls, Heading, SearchInput, IconButton } from '../../../shared/ui';

const CLIENTS_PAGE_SIZE = 10;
const CONSULTATIONS_PAGE_SIZE = 9;

export interface IClientTab {
  activeClient: number | null;
  setActiveClient: Dispatch<SetStateAction<number | null>>;
  clients: IClient[];
  consultations: IConsultation[];
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  onEditClient: (client: IClient) => void;
  /** Вкладка «Групповая терапия»: в подписях «клиент» → «встреча». */
  isGroupTherapy?: boolean;
  onSessionDetailModeChange?: (isSessionDetailMode: boolean) => void;
  /** Открытая сессия из текущей записи истории браузера. */
  pendingSessionDetail?: { consultationId: number; initialTab: string } | null;
  /** Консультации ещё грузятся — «сессия не найдена» показывать рано. */
  isSessionDetailPending?: boolean;
  onOpenSessionDetail: (consultationId: number, initialTab: string) => void;
  onCloseSessionDetail: () => void;
}

type Tab = 'all' | 'prep' | 'profile' | 'plan' | 'progress' | 'supervision';

const tabs: { key: Tab; label: string; wip?: boolean }[] = [
  { key: 'all', label: 'Все Сессии' },
  { key: 'prep', label: 'Подготовка', wip: true },
  { key: 'profile', label: 'Профиль клиента', wip: true },
  { key: 'plan', label: 'План терапии', wip: true },
  { key: 'progress', label: 'Прогресс', wip: true },
  { key: 'supervision', label: 'Для супервизии', wip: true },
];

export const ActiveClients: FC<IClientTab> = ({
  clients,
  consultations,
  search,
  setSearch,
  activeClient,
  setActiveClient,
  onEditClient,
  onSessionDetailModeChange,
  pendingSessionDetail = null,
  isSessionDetailPending = false,
  onOpenSessionDetail,
  onCloseSessionDetail,
  isGroupTherapy = false,
}) => {
  const [page, setPage] = useState(1);
  const [consultationsPage, setConsultationsPage] = useState(1);
  const [activeTab, setActiveTab] = useState<Tab>('all');
  // Открытая сессия — это запись в истории браузера (проп), а не локальное состояние:
  // так «назад» в браузере и «Назад к сессиям» ведут в одно и то же место.
  const sessionDetail = activeTab === 'all' ? pendingSessionDetail : null;

  const sortedClients = useMemo(() => {
    return [...clients].sort((a, b) => a.full_name.localeCompare(b.full_name, 'ru', { sensitivity: 'base' }));
  }, [clients]);

  const totalPages = useMemo(() => Math.ceil(sortedClients.length / CLIENTS_PAGE_SIZE), [sortedClients]);

  const sortedConsultations = useMemo(() => {
    if (!consultations) return [];
    return [...consultations].sort(
      (a, b) => new Date(b.datetime_start).getTime() - new Date(a.datetime_start).getTime(),
    );
  }, [consultations]);

  const consultationsTotalPages = useMemo(
    () => Math.ceil(sortedConsultations.length / CONSULTATIONS_PAGE_SIZE),
    [sortedConsultations.length],
  );

  useEffect(() => {
    setPage(1);
  }, [sortedClients]);

  useEffect(() => {
    setConsultationsPage(1);
  }, [consultations]);

  useEffect(() => {
    if (!activeClient) {
      return;
    }

    const index = sortedClients.findIndex((client) => client.id === activeClient);
    if (index === -1) {
      return;
    }

    const pageForClient = Math.floor(index / CLIENTS_PAGE_SIZE) + 1;
    setPage((prev) => (prev === pageForClient ? prev : pageForClient));
  }, [activeClient, sortedClients]);

  const paginatedClients = useMemo(() => {
    const start = (page - 1) * CLIENTS_PAGE_SIZE;
    return sortedClients.slice(start, start + CLIENTS_PAGE_SIZE);
  }, [sortedClients, page]);
  const consultationPages = useMemo(
    () => Array.from({ length: consultationsTotalPages }, (_, index) => index + 1),
    [consultationsTotalPages],
  );

  const paginatedConsultations = useMemo(() => {
    if (!sortedConsultations.length) return [];
    const start = (consultationsPage - 1) * CONSULTATIONS_PAGE_SIZE;
    return sortedConsultations.slice(start, start + CONSULTATIONS_PAGE_SIZE);
  }, [sortedConsultations, consultationsPage]);

  const selectedClientData = useMemo(
    () => (activeClient ? (sortedClients.find((c) => c.id === activeClient) ?? null) : null),
    [activeClient, sortedClients],
  );

  const sessionDetailConsultation = useMemo(
    () => (sessionDetail ? sortedConsultations.find((c) => c.id === sessionDetail.consultationId) : undefined),
    [sortedConsultations, sessionDetail],
  );
  const isSessionDetailMode = activeTab === 'all' && sessionDetail !== null;

  useEffect(() => {
    onSessionDetailModeChange?.(isSessionDetailMode);

    return () => {
      onSessionDetailModeChange?.(false);
    };
  }, [isSessionDetailMode, onSessionDetailModeChange]);

  useEffect(() => {
    if (pendingSessionDetail) setActiveTab('all');
  }, [pendingSessionDetail]);

  return (
    <div className="h-full w-full min-w-0 flex flex-col lg:flex-row bg-surface-muted pb-6 pt-4 gap-4 overflow-x-clip">
      {!isSessionDetailMode && (
        <div className="flex flex-col gap-5 w-64 shrink-0 pb-6 lg:pb-0 border-b border-b-ink-muted/40 lg:border-b-0">
          <div className="w-full bg-white border border-line rounded-control p-4 h-fit">
            <SearchInput
              aria-label={isGroupTherapy ? 'Поиск встречи' : 'Поиск клиента'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={isGroupTherapy ? 'Поиск встречи…' : 'Поиск клиента…'}
              className="mb-4"
            />

            <div className="flex items-center justify-between text-ui-xs text-ink-subtle mb-2 px-1">
              <span>Найдено:</span>
              <span>
                {clients.length}{' '}
                {declOfNum(
                  clients.length,
                  isGroupTherapy ? ['встреча', 'встречи', 'встреч'] : ['клиент', 'клиента', 'клиентов'],
                )}
              </span>
            </div>

            <div className="space-y-2">
              {paginatedClients.map((client) => (
                <div
                  key={client.id}
                  onClick={() => setActiveClient(client.id)}
                  className={classNames(
                    'w-full flex items-center justify-between px-3 py-2.5 rounded-control text-ui transition-colors cursor-pointer break-words gap-2',
                    activeClient === client.id
                      ? 'bg-primary text-white'
                      : 'bg-surface-muted text-ink-muted hover:bg-line',
                  )}
                >
                  <span className="min-w-0 flex-1 truncate text-left" title={client.full_name}>
                    {client.full_name}
                  </span>
                  {activeClient === client.id && (
                    <IconButton
                      variant="ghostInverse"
                      className="-my-1.5 shrink-0"
                      label={isGroupTherapy ? 'Редактировать встречу' : 'Редактировать клиента'}
                      onClick={(event) => {
                        event.stopPropagation();
                        setActiveClient(client.id);
                        onEditClient(client);
                      }}
                    >
                      <Edit2 size={14} className="shrink-0" />
                    </IconButton>
                  )}
                </div>
              ))}
            </div>

            <PaginationControls page={page} totalPages={totalPages} onPageChange={setPage} className="mt-5" />
          </div>
        </div>
      )}
      <div className="flex-1 min-w-0 overflow-visible">
        <div className="flex items-center justify-between mb-4">
          {!isSessionDetailMode && (
            <div className="flex items-center gap-3 min-w-0">
              <Heading size="lg" className="truncate">
                {selectedClientData?.full_name ?? (isGroupTherapy ? 'Выберите встречу' : 'Выберите клиента')}
              </Heading>
            </div>
          )}
          <div className={classNames('text-ui text-ink shrink-0', isSessionDetailMode ? 'ml-auto' : 'ml-2')}>
            {`${sortedConsultations.length} ${declOfNum(sortedConsultations.length || 0, ['сессия', 'сессии', 'сессий'])}`}
          </div>
        </div>

        {!isSessionDetailMode && (
          <TabList
            variant="pills"
            className="mb-5"
            value={activeTab}
            onChange={setActiveTab}
            items={tabs.map((tab) => ({
              id: tab.key,
              hint: tab.wip ? 'Раздел в разработке — показан пример' : undefined,
              label: (
                <>
                  {tab.label}
                  {tab.key === 'supervision' && (
                    <ChevronDown size={12} className="inline shrink-0 opacity-90" aria-hidden />
                  )}
                  {tab.wip && <span className="ml-0.5 text-ui-xs leading-none">🚧</span>}
                </>
              ),
            }))}
          />
        )}

        {activeTab === 'all' ? (
          <>
            {sessionDetail && sessionDetailConsultation ? (
              <SessionDetail
                consultationId={sessionDetail.consultationId}
                session={buildSessionDetailSession(sessionDetailConsultation)}
                initialTab={sessionDetail.initialTab}
                clientName={selectedClientData?.full_name}
                onBack={onCloseSessionDetail}
              />
            ) : sessionDetail ? (
              <EmptyState>{isSessionDetailPending ? 'Загружаем сессию…' : 'Сессия не найдена в списке'}</EmptyState>
            ) : (
              <>
                <AllSessions consultations={paginatedConsultations} onOpenSessionDetail={onOpenSessionDetail} />
                <PaginationControls
                  page={consultationsPage}
                  totalPages={consultationsTotalPages}
                  onPageChange={setConsultationsPage}
                  className="mt-5"
                />
              </>
            )}
          </>
        ) : activeTab === 'prep' ? (
          <PrepHighlights clientName={selectedClientData?.full_name} />
        ) : activeTab === 'profile' ? (
          <ClientProfile />
        ) : activeTab === 'plan' ? (
          <TreatmentPlan />
        ) : activeTab === 'progress' ? (
          <Progress />
        ) : activeTab === 'supervision' ? (
          <Supervision />
        ) : (
          <EmptyState>Раздел в разработке</EmptyState>
        )}
      </div>
    </div>
  );
};
